// The wormhole by Saturn, drawn the way Interstellar drew it: a spherical
// gravitational lens with no surface. Rays inside the throat's critical
// impact parameter go through and show another galaxy; the rest bend round
// the mouth and show our own sky, lensed into an Einstein ring just outside
// the edge, with the bending dying away smoothly into the surrounding stars.
//
// Physics: the Dneg wormhole of James, von Tunzelmann, Franklin & Thorne
// (2015, "Visualizing Interstellar's Wormhole", Am. J. Phys. 83, 486), with
// the film's parameters (Sec. V): length 2a = 0.01 rho and lensing width
// W = 0.05 rho, where W = 1.42953 M. Its spatial metric is
//   dl^2 + r(l)^2 dOmega^2,  r = rho for |l| <= a, and beyond
//   r = rho + M [x atan x - ln(1 + x^2) / 2],  x = 2(|l| - a) / (pi M).
// r never drops below rho, so the critical impact parameter is rho itself.
//
// A ray moves in a plane and conserves its angular momentum, the impact
// parameter b, so the angle it sweeps is the quadrature
//   dphi = b dl / (r sqrt(r^2 - b^2)).
// Cumulating that once per b from the ray's turning point (b > rho) or the
// throat (b < rho) gives, for a camera at areal radius d:
//   heading in:  phi = T(d, b) + K(b)   (through to the far side if b < rho)
//   heading out: phi = K(b) - T(d, b)
// with T = the integral from the turning point to the camera and K = the
// integral from there to infinity (the wormhole is symmetric, so the far
// half of a ray through the throat is K again). One float texture holds K
// and T less flat space's share, acos(b / d); the fragment shader
// (src/shaders/wormhole.js) turns each camera ray through phi and reads the
// right sky.
//
// Our side's sky is a cube render of the scene from the mouth (stars,
// Saturn, the Sun), one face per frame; the far side's is a galaxy painted
// once on the GPU.

import * as THREE from 'three';
import {
  wormholeLensVertexShader,
  wormholeLensFragmentShader,
  farGalaxyVertexShader,
  farGalaxyFragmentShader,
} from './shaders/wormhole.js';

const LENS_A = 0.005;               // half-length of the throat, rho = 1
const LENS_M = 0.05 / 1.42953;      // lensing width W = 0.05 rho

// Table layout. Rows: camera distance d (throat radii), d - 1 spaced
// logarithmically from the throat out to D_MAX. Columns: impact parameter
// b, spaced by asinh about the critical b = 1 where rays wind round the
// throat. The last row holds K(b). The shader repeats these mappings.
const ROWS = 112;
const COLS = 640;
const H0 = 0.004;
const D_MAX = 400;
const BW = 0.004;
const B_MAX = 400;
const U0 = Math.asinh(-1 / BW);
const U1 = Math.asinh((B_MAX - 1) / BW);
const LROW = Math.log1p((D_MAX - 1) / H0);
const STEPS = 1200;                 // Simpson steps per column

// The far-side galaxy texture, painted in strips over a few frames.
const FAR_WIDTH = 2048;
const FAR_HEIGHT = 1024;
const FAR_STRIPS = 8;

function radiusAt(l) {
  const L = Math.abs(l);
  if (L <= LENS_A) return 1;
  const x = 2 * (L - LENS_A) / (Math.PI * LENS_M);
  return 1 + LENS_M * (x * Math.atan(x) - 0.5 * Math.log1p(x * x));
}

function slopeAt(l) {
  const L = Math.abs(l);
  if (L <= LENS_A) return 0;
  return (2 / Math.PI) * Math.atan(2 * (L - LENS_A) / (Math.PI * LENS_M));
}

// Proper distance l >= 0 at which the areal radius reaches R.
function distanceAt(R) {
  if (R <= 1) return 0;
  let lo = LENS_A;
  let hi = LENS_A + R + 10;
  for (let i = 0; i < 70; i++) {
    const mid = 0.5 * (lo + hi);
    if (radiusAt(mid) < R) lo = mid; else hi = mid;
  }
  return 0.5 * (lo + hi);
}

const rowDistance = (i) => 1 + H0 * Math.expm1((i / (ROWS - 1)) * LROW);
const columnImpact = (j) => 1 + BW * Math.sinh(U0 + (j / (COLS - 1)) * (U1 - U0));

// The swept angle from the turning point (or the throat) for impact
// parameter b, at each proper distance in ls (ascending), and on to
// infinity. Integrated in s with l = l0 + s^2, which takes the 1/sqrt
// singularity out of the turning point; s itself is spaced as u^2 to put
// steps where the throat bends the ray hardest.
function sweep(b, ls, lEnd, out, stride, offset) {
  const l0 = b > 1 ? distanceAt(b) : 0;
  const S = Math.sqrt(lEnd - l0);
  const f = (s) => {
    const R = radiusAt(l0 + s * s);
    const q = R * R - b * b;
    return q > 0 ? (2 * s * b) / (R * Math.sqrt(q)) : 0;
  };
  let fPrev;
  if (b > 1) {
    const k = slopeAt(l0);
    fPrev = k > 0 ? 2 / Math.sqrt(2 * b * k) : 0;
  } else {
    fPrev = f(0);
  }
  let k = 0;
  while (k < ls.length && ls[k] <= l0) { out[offset + k * stride] = 0; k++; }
  let acc = 0;
  let sPrev = 0;
  let lPrev = l0;
  for (let i = 1; i <= STEPS; i++) {
    const u = i / STEPS;
    const s = S * u * u;
    const fm = f(0.5 * (sPrev + s));
    const fs = f(s);
    const seg = ((s - sPrev) / 6) * (fPrev + 4 * fm + fs);
    const lCur = l0 + s * s;
    while (k < ls.length && ls[k] <= lCur) {
      const t = (ls[k] - lPrev) / Math.max(lCur - lPrev, 1e-30);
      out[offset + k * stride] = acc + seg * t;
      k++;
    }
    acc += seg;
    sPrev = s;
    fPrev = fs;
    lPrev = lCur;
  }
  while (k < ls.length) { out[offset + k * stride] = acc; k++; }
  // Past lEnd space is flat to well under a part in 10^4.
  return acc + Math.asin(Math.min(1, b / radiusAt(lEnd)));
}

export function buildLensTable() {
  const data = new Float32Array(COLS * (ROWS + 1));
  const ls = new Float64Array(ROWS);
  for (let i = 0; i < ROWS; i++) ls[i] = distanceAt(rowDistance(i));
  const lEnd = distanceAt(2 * D_MAX);
  for (let j = 0; j < COLS; j++) {
    const b = columnImpact(j);
    const K = sweep(b, ls, lEnd, data, COLS, j);
    // Stored as what the bending adds to flat space's acos(b / d), which
    // takes T's square-root rise off the turning point and leaves a smooth
    // residual to interpolate; the shader adds acos(b / d) back exactly. A
    // camera nearer than b sits inside the ray's turning point: T = 0.
    for (let i = 0; i < ROWS; i++) {
      const d = rowDistance(i);
      const k = i * COLS + j;
      data[k] = d <= b ? 0 : data[k] - Math.acos(b / d);
    }
    data[ROWS * COLS + j] = K;
  }
  const texture = new THREE.DataTexture(data, COLS, ROWS + 1, THREE.RedFormat, THREE.FloatType);
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

const LENS_DEFINES = {
  LENS_ROWS: ROWS,
  LENS_COLS: COLS,
  LENS_H0: H0.toFixed(6),
  LENS_LROW: LROW.toFixed(8),
  LENS_D_MAX: D_MAX.toFixed(1),
  LENS_BW: BW.toFixed(6),
  LENS_B_MAX: B_MAX.toFixed(1),
  LENS_U0: U0.toFixed(8),
  LENS_U1: U1.toFixed(8),
};

function canRenderHalfFloat(renderer) {
  const ext = renderer.extensions;
  return ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float');
}

// Paints the far galaxy into an equirectangular target, one horizontal
// strip per call so no single frame pays for it. Returns true when done.
function createFarGalaxyPainter(renderer) {
  const type = canRenderHalfFloat(renderer) ? THREE.HalfFloatType : THREE.UnsignedByteType;
  const target = new THREE.WebGLRenderTarget(FAR_WIDTH, FAR_HEIGHT, {
    type,
    format: THREE.RGBAFormat,
    depthBuffer: false,
    generateMipmaps: false,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: THREE.RepeatWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
  });
  target.texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  const material = new THREE.ShaderMaterial({
    uniforms: { uSize: { value: new THREE.Vector2(FAR_WIDTH, FAR_HEIGHT) } },
    vertexShader: farGalaxyVertexShader,
    fragmentShader: farGalaxyFragmentShader,
    depthTest: false,
    depthWrite: false,
  });
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(quad);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  let strip = 0;
  const step = () => {
    if (strip >= FAR_STRIPS) return true;
    const prev = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevScissorTest = target.scissorTest;
    const h = FAR_HEIGHT / FAR_STRIPS;
    target.scissor.set(0, strip * h, FAR_WIDTH, h);
    target.scissorTest = true;
    // Mipmaps once, after the last strip.
    target.texture.generateMipmaps = strip === FAR_STRIPS - 1;
    renderer.autoClear = false;
    renderer.setRenderTarget(target);
    renderer.render(scene, camera);
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAutoClear;
    target.scissorTest = prevScissorTest;
    strip++;
    if (strip >= FAR_STRIPS) {
      target.scissorTest = false;
      quad.geometry.dispose();
      material.dispose();
      return true;
    }
    return false;
  };
  return { texture: target.texture, target, step };
}

const _scale = new THREE.Vector3();
const _frustum = new THREE.Frustum();
const _projView = new THREE.Matrix4();
const _sphere = new THREE.Sphere();
const _up = new THREE.Vector3();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _z = new THREE.Vector3();

/**
 * @param {object} opts
 * @param {THREE.WebGLRenderer} opts.renderer
 * @param {THREE.Scene} opts.scene      rendered into the local-sky cube
 * @param {number} [opts.cubeSize]      local-sky cube face size
 */
export function createWormholeLens({ renderer, scene, cubeSize = 1024 }) {
  const cubeTarget = new THREE.WebGLCubeRenderTarget(cubeSize, {
    type: canRenderHalfFloat(renderer) ? THREE.HalfFloatType : THREE.UnsignedByteType,
    format: THREE.RGBAFormat,
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
  });
  cubeTarget.texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  // near/far match the main camera, so the cube sees what the scene shows:
  // the starfield sphere at 4e7 included.
  const cubeCamera = new THREE.CubeCamera(1, 50000000, cubeTarget);
  scene.add(cubeCamera);

  const placeholder = new THREE.DataTexture(new Float32Array([0, 0, 0, 0]), 1, 1, THREE.RedFormat, THREE.FloatType);
  placeholder.needsUpdate = true;
  const material = new THREE.ShaderMaterial({
    defines: LENS_DEFINES,
    uniforms: {
      uTable: { value: placeholder },
      uLocalSky: { value: cubeTarget.texture },
      uFarSky: { value: null },
      uFarFrame: { value: new THREE.Matrix3() },
      uCenterView: { value: new THREE.Vector3() },
      uRho: { value: 1 },
      uMeshRadius: { value: 24 },
      uFarGain: { value: 1.6 },
      uFade: { value: 0 },
    },
    vertexShader: wormholeLensVertexShader,
    fragmentShader: wormholeLensFragmentShader,
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    toneMapped: false,
  });

  const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 48, 24), material);
  mesh.name = 'wormhole gravitational lens';
  // Draw after every sky layer (skybox -1000, star points -999.x).
  mesh.renderOrder = 18;
  // Many throat radii across and drawn from inside: never a click target.
  mesh.raycast = () => {};
  mesh.visible = false;
  // The centre in view space comes from the camera actually drawing it, so
  // it never lags the frame's last camera move.
  mesh.onBeforeRender = (_r, _s, cam) => {
    material.uniforms.uCenterView.value.setFromMatrixPosition(mesh.matrixWorld).applyMatrix4(cam.matrixWorldInverse);
  };

  let table = null;
  let painter = null;
  let farReady = false;
  let face = 0;
  let cubeFilled = false;
  let idle = null;

  const buildTable = () => {
    if (table) return;
    table = buildLensTable();
    material.uniforms.uTable.value = table;
    if (idle !== null && typeof cancelIdleCallback === 'function') cancelIdleCallback(idle);
    idle = null;
  };
  // The table costs a few tens of milliseconds: build it when the page is
  // idle, or right away if the wormhole comes into view first.
  if (typeof requestIdleCallback === 'function') idle = requestIdleCallback(buildTable, { timeout: 8000 });

  // The star layers draw sprites a fixed number of pixels across (at least
  // 2.5), and a cube texel spans more sky than a screen pixel, so in the
  // cube every star would come out bigger than on screen. Their pixel
  // ratio is scaled by the ratio of the two for the cube's renders.
  let starLayers = null;
  let starScale = 1;
  let starScan = 0;
  function findStarLayers() {
    starLayers = [];
    scene.traverse((o) => {
      if (o.isPoints && o.material && o.material.uniforms && o.material.uniforms.pixelRatio
        && (o.material.uniforms.sizeScale || (o.material.userData && o.material.userData.starPoints))) {
        starLayers.push(o.material.uniforms.pixelRatio);
      }
    });
  }
  function withCubeStars(fn) {
    // The catalogue arrives asynchronously, so look again now and then.
    if (!starLayers || starScan++ % 600 === 0) findStarLayers();
    const saved = starLayers.map((u) => u.value);
    starLayers.forEach((u) => { u.value *= starScale; });
    fn();
    starLayers.forEach((u, i) => { u.value = saved[i]; });
  }

  function renderCubeFace(i) {
    const cams = cubeCamera.children;
    const prev = renderer.getRenderTarget();
    const prevFace = renderer.getActiveCubeFace();
    const prevMip = renderer.getActiveMipmapLevel();
    const prevXr = renderer.xr.enabled;
    renderer.xr.enabled = false;
    cubeTarget.texture.generateMipmaps = i === 5;
    renderer.setRenderTarget(cubeTarget, i);
    renderer.render(scene, cams[i]);
    cubeTarget.texture.generateMipmaps = true;
    renderer.setRenderTarget(prev, prevFace, prevMip);
    renderer.xr.enabled = prevXr;
  }

  // The far galaxy's frame: its centre lies a little off the direction
  // seen straight through the mouth from the Sun's side, its plane tilted
  // across the view.
  function setFarFrame(center) {
    _up.set(0, 1, 0);
    // Galactic centre: the Sun -> wormhole direction (what a camera on the
    // Sun's side sees straight through), turned 24 degrees off axis.
    _x.copy(center).normalize().applyAxisAngle(_up, 0.42);
    // Galactic north: world up made square to the centre, then tilted so
    // the band crosses the mouth on a slant.
    _y.copy(_up).addScaledVector(_x, -_up.dot(_x)).normalize().applyAxisAngle(_x, 0.62);
    _z.crossVectors(_x, _y);
    // Rows are the galaxy's axes in world coordinates: world -> galaxy.
    material.uniforms.uFarFrame.value.set(
      _x.x, _x.y, _x.z,
      _y.x, _y.y, _y.z,
      _z.x, _z.y, _z.z,
    );
  }

  /**
   * Per frame, after the wormhole's world transform is current.
   * @param {object} o
   * @param {THREE.PerspectiveCamera} o.camera
   * @param {THREE.Vector3} o.center   wormhole centre, world
   * @param {number} o.rho             throat radius, world units
   * @param {number} o.viewportHeight  drawing-buffer height in pixels
   * @param {boolean} o.visible        the wormhole is shown at all
   */
  function update({ camera, center, rho, viewportHeight, visible }) {
    if (!visible) { mesh.visible = false; return; }

    // Lens extent: out to where our sky's displacement (~2M/b radians, the
    // far-field bending of this metric) falls to a few pixels.
    const pixel = (camera.fov * Math.PI / 180) / Math.max(1, viewportHeight);
    const meshRadius = THREE.MathUtils.clamp((2 * LENS_M) / (5 * pixel), 6, 60);
    material.uniforms.uMeshRadius.value = meshRadius;
    material.uniforms.uRho.value = rho;
    mesh.scale.setScalar(meshRadius * rho / Math.max(mesh.parent ? mesh.parent.getWorldScale(_scale).x : 1, 1e-9));

    // Below a couple of pixels across there is nothing to see.
    const mouthPixels = rho / Math.max(camera.position.distanceTo(center), rho) / pixel;
    if (mouthPixels < 1.5) { mesh.visible = false; return; }

    _projView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_projView);
    _sphere.set(center, meshRadius * rho);
    const inView = _frustum.intersectsSphere(_sphere);
    if (!inView) { mesh.visible = false; return; }

    if (!table) buildTable();
    if (!painter) { painter = createFarGalaxyPainter(renderer); setFarFrame(center); }
    if (!farReady) {
      farReady = painter.step();
      if (farReady) material.uniforms.uFarSky.value = painter.texture;
    }

    // Our side's sky: the scene from the mouth, without the lens itself.
    cubeCamera.position.copy(center);
    cubeCamera.updateMatrixWorld(true);
    mesh.visible = false;
    starScale = pixel / ((Math.PI / 2) / cubeSize);
    withCubeStars(() => {
      if (cubeCamera.coordinateSystem !== renderer.coordinateSystem || !cubeFilled) {
        cubeCamera.update(renderer, scene);
        cubeFilled = true;
      } else {
        renderCubeFace(face);
        face = (face + 1) % 6;
      }
    });

    mesh.visible = farReady;
    if (farReady) {
      const fade = material.uniforms.uFade;
      fade.value = Math.min(1, fade.value + 0.08);
    }
  }

  function dispose() {
    if (idle !== null && typeof cancelIdleCallback === 'function') cancelIdleCallback(idle);
    scene.remove(cubeCamera);
    cubeTarget.dispose();
    if (painter) painter.target.dispose();
    if (table) table.dispose();
    placeholder.dispose();
    mesh.geometry.dispose();
    material.dispose();
  }

  return { mesh, update, dispose };
}
